import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';

import { Button } from 'V2/Shared/Button';
import { Modal } from 'V2/Shared/Modal';
import { fromCamelToKebab } from 'V2/Utilities/StringHelpers';

import {
  FeeAction,
  FeeBreakdownModalProps,
  FeeBreakdownProps,
  FeeLabel,
} from './types';
import styles from './styles.module.css';

const FeeBreakdownModal: React.FC<FeeBreakdownModalProps> = ({
  show,
  sections,
  closeModal,
}) => {
  const renderLabelAsText = (label: FeeLabel) => {
    return <span>{label}</span>;
  };

  const renderLabelAsButton = (label: FeeLabel, action: FeeAction) => {
    return (
      <button
        type="button"
        onClick={action}
        className={styles.itemActionButton}
      >
        {label}
      </button>
    );
  };

  return (
    <Modal title="Fee breakdown" showModal={show}>
      <div className={styles.feeBreakdownModalContent}>
        {sections.map((section, idx) => (
          <section
            key={idx}
            className={styles.feeBreakdownSection}
            data-testid={`fee-breakdown-section-${section.sectionName}`}
          >
            <h3
              data-testid="fee-breakdown-section-title"
              className={styles.breakdownTitle}
            >
              {section.title}
            </h3>

            <ul className={styles.breakdownList}>
              {section.items.map((item, idx) => (
                <li
                  key={idx}
                  data-testid={`fee-breakdown-line${
                    item.id ? '-' + fromCamelToKebab(item.id) : ''
                  }`}
                  className={styles.breakdownListItem}
                >
                  <div className={styles.breakdownItem}>
                    {item.action
                      ? renderLabelAsButton(item.label, item.action)
                      : renderLabelAsText(item.label)}
                  </div>

                  <div
                    data-testid="fee-breakdown-line-amount"
                    className={styles.breakdownAmount}
                  >
                    {item.amount}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <div className={styles.breakdownModalButtonContainer}>
          <Button
            variant="contained"
            size="md"
            onClick={closeModal}
            data-testid="fee-breakdown-close-button"
          >
            Ok
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const useFeeBreakdownModal = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalProps, setModalProps] = useState<FeeBreakdownProps>({
    sections: [],
  });

  const showModal = useCallback((props: FeeBreakdownProps) => {
    setModalProps(props);
    setIsModalVisible(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalProps({ sections: [] });
    setIsModalVisible(false);
  }, []);

  const modalComponent = isModalVisible
    ? createPortal(
        <FeeBreakdownModal
          show={isModalVisible}
          closeModal={closeModal}
          {...modalProps}
        />,
        document.body
      )
    : null;

  return {
    showFeeBreakdownModal: showModal,
    closeFeeBreakdownModal: closeModal,
    FeeBreakdownModalComponent: modalComponent,
  };
};
